<template>
  <BaseModal
    :show="show"
    title="Parental Consent Required"
    max-width="632"
    :persistent="true"
    @update:show="$emit('update:show', $event)"
    @close="handleClose"
  >
    <!-- Description -->
    <div class="mb-6" style="font-size: 16px; line-height: 1.5; color: #191919">
      Parental consent is required to pay the {{ transactionFee }} for
      <span style="background-color: #ffc627; font-weight: bold">{{ studentName || "[Student's Full name]" }}</span
      >.
    </div>

    <!-- Consent Checkbox Section -->
    <div class="mb-6" style="border: 1px solid #dddddd; padding: 16px">
      <v-checkbox
        v-model="consent1"
        hide-details
        color="black"
        class="parent-consent-checkbox"
        @change="asuoDataLayerClick('parentConsent', '0')"
      >
        <template #label>
          <div style="color: rgba(0, 0, 0, 0.54); font-size: 14px; line-height: 1.43">
            <p style="margin-bottom: 12px">
              I am the parent or legal guardian of the minor named above. I have the legal right to provide this consent
              and, by signing below, I acknowledge and agree to the following:
            </p>

            <ul style="margin: 16px 0; padding-left: 20px; list-style-type: disc">
              <li style="margin-bottom: 12px">
                I understand that by enrolling my child in this course, they will be participating in college-level
                coursework, which may include advanced concepts, mature themes, or content that may not be suitable for
                all minors.
              </li>

              <li style="margin-bottom: 12px">
                I consent to my child's enrollment in this course and accept responsibility for reviewing and approving
                their participation.
              </li>

              <li style="margin-bottom: 12px">
                I have reviewed the syllabus for this specific course and understand the academic expectations and
                subject matter involved.
              </li>

              <li style="margin-bottom: 12px">
                I consent to the terms and conditions of this course enrollment fee, as set forth in the
                <a
                  :href="ulcPartnerFlag ? 'https://ulc.asu.edu/terms-of-use/' : 'https://ea.asu.edu/terms-of-use/'"
                  style="color: #8c1d40; text-decoration: underline"
                  target="_blank"
                  rel="noopener"
                  @click="asuoDataLayerClick('parentConsent', '1')"
                  >ULC Terms of Use</a
                >.
              </li>
            </ul>
          </div>
        </template>
      </v-checkbox>
    </div>

    <!-- Parent or Legal Guardian Form -->
    <div class="mb-6">
      <div class="font-weight-bold mb-2" style="font-size: 16px; color: #000000">Parent or Legal Guardian</div>

      <v-row no-gutters style="gap: 24px">
        <v-col cols="12" sm="6" style="flex: 1">
          <!-- First Name -->
          <div class="mb-2" style="font-size: 16px; color: #191919">First Name</div>
          <v-text-field
            v-model="firstName"
            placeholder="First Name"
            variant="outlined"
            density="comfortable"
            hide-details
            style="font-size: 16px"
            @click="asuoDataLayerClick('parentConsent', '2')"
          />
        </v-col>

        <v-col cols="12" sm="6" style="flex: 1">
          <!-- Last Name -->
          <div class="mb-2" style="font-size: 16px; color: #191919">Last Name</div>
          <v-text-field
            v-model="lastName"
            placeholder="Last Name"
            variant="outlined"
            density="comfortable"
            hide-details
            style="font-size: 16px"
            @click="asuoDataLayerClick('parentConsent', '3')"
          />
        </v-col>
      </v-row>
    </div>

    <!-- CTA Button -->
    <UiButton
      variant="gold"
      size="large"
      block
      :disabled="!isFormValid"
      @click="
        handleConsent();
        asuoDataLayerClick('parentConsent', '4', { first_name: firstName, last_name: lastName });
      "
      :style="{ borderRadius: '100px', opacity: isFormValid ? 1 : 0.65 }"
    >
      Consent and continue to payment
    </UiButton>
  </BaseModal>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import { BaseModal, Button as UiButton } from './ui/index.js';
import { Helper } from '../mixins/helpers.js';
import { DataLayerMixin } from '../mixins/dataLayerMixin.js';

export default defineComponent({
  name: 'ParentConsentModal',

  components: {
    BaseModal,
    UiButton
  },

  mixins: [Helper, DataLayerMixin],

  props: {
    show: {
      type: Boolean,
      default: false
    },
    studentName: {
      type: String,
      default: ''
    },
    transactionFee: {
      type: String,
      default: 'enrollment fee'
    }
  },

  emits: ['update:show', 'consent', 'cancel'],

  setup(props, { emit }) {
    const consent1 = ref(false);
    const firstName = ref('');
    const lastName = ref('');

    const isFormValid = computed(() => {
      return consent1.value && firstName.value.trim() !== '' && lastName.value.trim() !== '';
    });

    const handleConsent = () => {
      if (isFormValid.value) {
        emit('consent', {
          firstName: firstName.value,
          lastName: lastName.value
        });
      }
    };

    const handleClose = () => {
      emit('cancel');
      emit('update:show', false);
    };

    return {
      consent1,
      firstName,
      lastName,
      isFormValid,
      handleConsent,
      handleClose
    };
  },

  computed: {
    ulcPartnerFlag() {
      return Helper.methods.isUlcPartner();
    }
  }
});
</script>

<style scoped>
/* Custom checkbox styling to match design */
.parent-consent-checkbox :deep(.v-selection-control) {
  align-items: flex-start !important;
  min-height: auto;
}

.parent-consent-checkbox :deep(.v-selection-control__wrapper) {
  margin-top: 4px;
}

.parent-consent-checkbox :deep(.v-selection-control__input) {
  width: 24px;
  height: 24px;
  align-self: flex-start !important;
  margin-top: 0 !important;
}

.parent-consent-checkbox :deep(.v-label) {
  opacity: 1;
  margin-left: 8px;
}

/* Ensure text field borders match design */
:deep(.v-field--variant-outlined .v-field__outline__start),
:deep(.v-field--variant-outlined .v-field__outline__end) {
  border-color: #d0d0d0;
}
</style>
